import React, { useMemo, ReactNode, ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from 'prodivers/layout';
import { Header, Title } from 'ui-library';
import { getClassNames } from 'utils';
import Footer from '../Footer';
import PageTitle from '../PageTitle';
import TopNavigation from '../TopNavigation';
import './Layout.css';

interface HeaderState {
    link?: string;
    content?: ElementType;
    contentProps?: Record<string, any>;
    clientName?: string;
    dfsClientId?: string;
    externalId?: string;
    leftButton?: object;
    rightButton?: object;
    navigation?: any;
    profileInfo?: any;
    navigationTablet?: any;
    showClientTitle?: boolean;
    showTopNavigation?: boolean;
    headComponent?: ReactNode;
    containerClassName?: string;
    onLeftButtonClick?: () => void;
    onRightButtonClick?: () => void;
    isDataAccessible?: boolean;
    dateOfDeath?: string | Date | null;
}

interface LayoutProps {
    children?: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children = null }) => {
    const { t } = useTranslation();

    const { headerState } = useLayout() as { headerState: HeaderState };

    const {
        link,
        content: Content,
        contentProps = {},
        clientName,
        dfsClientId,
        externalId,
        leftButton,
        rightButton,
        navigation,
        profileInfo,
        navigationTablet,
        showClientTitle,
        showTopNavigation,
        headComponent,
        containerClassName,
        onLeftButtonClick,
        onRightButtonClick,
        isDataAccessible,
        dateOfDeath,
    } = headerState;

    const classNames = useMemo(
        () => getClassNames('', containerClassName || ''),
        [containerClassName],
    );

    return (
        <>
            <Header
                link={link}
                title={t('navigation.title')}
                content={Content ? <Content {...contentProps} /> : undefined}
                navigation={navigation}
                profileInfo={profileInfo}
                navigationTablet={navigationTablet}
                leftButton={leftButton}
                rightButton={rightButton || {}}
                onLeftButtonClick={onLeftButtonClick}
                onRightButtonClick={onRightButtonClick}
            />
            <main className={classNames}>
                {showClientTitle && (
                    <PageTitle>
                        <>
                            {clientName}
                            {' '}
                            {dateOfDeath ? <sup>†</sup> : ''}
                        </>
                    </PageTitle>
                )}

                {showClientTitle && externalId && (
                    <Title className="client-dashboard--client-id" type={2}>
                        {externalId}
                    </Title>
                )}

                {(dfsClientId && showTopNavigation) && (
                    <TopNavigation
                        clientId={dfsClientId}
                        isDataAccessible={isDataAccessible as boolean}
                    />
                )}

                {headComponent}
                {children}
            </main>
            <Footer />
        </>
    );
};

export default Layout;
